import { AxiosError } from "axios";
import {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  GenericErrorResponse,
  MarginTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import { TEXT_COLORS } from "../../../constants/design/colors";
import { sideColors } from "../../../utils/sideColors";
import { MarketInstrumentContext } from "../../../contexts/MarketInstrumentContext";
import { useGetAccount } from "../../../hooks/api/account/useGetAccount";
import { useMarginType } from "../../../hooks/api/margin/useMarginType";
import { useToast } from "../../../hooks/toast";
import { normalizeMarginType } from "../../../utils/margin";
import { ToastEnum, ToastStatusEnum } from "../../../utils/toast";
import { IPerpsMarket } from "../../../contexts/MarketInstrumentContext/useGetMarkets";
import { IPerpsFormFieldValues, PerpFormFieldKeyEnum } from "./form.types";
import { resolveMaxLeverage } from "../../../utils/leverage";

export function useMarginSelector({
  form,
  side,
  instrument,
}: {
  form: UseFormReturn<IPerpsFormFieldValues, any>;
  side: SideResponse;
  instrument?: IPerpsMarket;
}) {
  const { t } = useTranslation("app", {
    keyPrefix: "TradeForm.PerpsTradeForm.Form",
  });
  const [showLeverageModal, setShowLeverageModal] = useState(false);
  const [showMarginModal, setShowMarginModal] = useState(false);
  const [learnMoreMarginType, setLearnMoreMarginType] =
    useState<MarginTypeResponse>();
  const userSelectedMarginType = useRef<MarginTypeResponse | null>(null);
  const marginType = form.watch(PerpFormFieldKeyEnum.MARGIN_TYPE);
  const { data: accountData, mutate: mutateAccount } = useGetAccount();

  const activeColor = sideColors(side).one;
  const activeBg = sideColors(side).four;

  const getMarginButtonStyle = (type: MarginTypeResponse): CSSProperties => {
    const isActive = marginType?.toUpperCase() === type;
    return {
      color: isActive ? activeColor : TEXT_COLORS.three,
      backgroundColor: isActive ? activeBg : "transparent",
      boxShadow: isActive
        ? `0px 0px 0px 1px ${activeColor} inset`
        : "0px 0px 0px 1px rgba(255, 255, 255, 0.15) inset",
    };
  };
  const { mutateMarkets } = useContext(MarketInstrumentContext);
  const { updateMarginType } = useMarginType();
  const { addToast, addErrorToast } = useToast();
  const accountLeverage = useMemo(
    () =>
      accountData?.leverages?.find(
        (lev) =>
          lev.instrument_id?.toLowerCase() ===
          instrument?.instrument_id?.toLowerCase()
      )?.leverage,
    [accountData?.leverages, instrument?.instrument_id]
  );
  const formLeverage = form.watch(PerpFormFieldKeyEnum.LEVERAGE);
  // Shown on the leverage buttons when /markets gives no max_leverage.
  const leverageUnavailableText =
    resolveMaxLeverage(instrument) === undefined
      ? t("max_leverage_unavailable")
      : undefined;
  const displayLeverage = useMemo(() => {
    const lev = Number(formLeverage || accountLeverage || 1);
    return Number.isFinite(lev) && lev > 0 ? Math.round(lev) : 1;
  }, [accountLeverage, formLeverage]);

  // Clear user-selected margin type when instrument changes
  useEffect(() => {
    userSelectedMarginType.current = null;
  }, [instrument?.instrument_id]);

  useEffect(() => {
    // Don't override an explicit user selection with stale server data
    if (userSelectedMarginType.current) {
      form.setValue(PerpFormFieldKeyEnum.MARGIN_TYPE, userSelectedMarginType.current);
      return;
    }

    const existingPosition = accountData?.positions?.find(
      (pos) => pos.instrument_id === instrument?.instrument_id
    );

    // If there is an existing position, default to that position's margin type.
    const existingMarginType = normalizeMarginType(existingPosition?.margin_type);

    if (existingMarginType) {
      form.setValue(PerpFormFieldKeyEnum.MARGIN_TYPE, existingMarginType);
    } else {
      // Otherwise, set it to account.leverage margin type.
      const accountMarginType = accountData?.leverages?.find(
        (lev) => lev.instrument_id === instrument?.instrument_id
      )?.margin_type;

      const normalizedAccountMarginType = normalizeMarginType(accountMarginType);

      if (normalizedAccountMarginType) {
        form.setValue(
          PerpFormFieldKeyEnum.MARGIN_TYPE,
          normalizedAccountMarginType
        );
      }
    }
  }, [accountData, accountData?.positions, form, instrument?.instrument_id]);

  const onUpdateMarginType = useCallback(
    async (type: MarginTypeResponse) => {
      if (marginType === type) {
        return;
      }
      if (!accountData) {
        return;
      }

      const previousMarginType =
        normalizeMarginType(marginType) || MarginTypeResponse.Cross;
      userSelectedMarginType.current = type;
      form.setValue(PerpFormFieldKeyEnum.MARGIN_TYPE, type, {
        shouldDirty: true,
        shouldValidate: true,
      });

      try {
        if (instrument?.instrument_id) {
          await updateMarginType(String(instrument.instrument_id), type);
          mutateAccount();
          addToast(
            {
              header: t("updated_margin_type_success"),
              status: ToastStatusEnum.SUCCESS,
              type: ToastEnum.SIMPLE,
            },
            3000
          );
        }
      } catch (error) {
        userSelectedMarginType.current = null;
        form.setValue(PerpFormFieldKeyEnum.MARGIN_TYPE, previousMarginType, {
          shouldDirty: true,
          shouldValidate: true,
        });
        const genericResponseAxiosError =
          error as AxiosError<GenericErrorResponse>;
        addErrorToast(
          "Error updating margin type",
          genericResponseAxiosError.response?.data?.error || ""
        );
      }
    },
    [
      accountData,
      addErrorToast,
      addToast,
      form,
      instrument?.instrument_id,
      marginType,
      mutateAccount,
      t,
      updateMarginType,
    ]
  );

  const onUpdateLeverageSuccess = useCallback(() => {
    mutateAccount();
    mutateMarkets?.();
  }, [mutateAccount, mutateMarkets]);

  const onLearnMore = useCallback((type: MarginTypeResponse) => {
    setShowMarginModal(true);
    setLearnMoreMarginType(type);
  }, []);

  const onHideMarginModal = useCallback(() => {
    setShowMarginModal(false);
    setLearnMoreMarginType(undefined);
  }, []);

  const isCross = marginType?.toUpperCase() === MarginTypeResponse.Cross;

  return {
    t,
    showLeverageModal,
    setShowLeverageModal,
    showMarginModal,
    onHideMarginModal,
    learnMoreMarginType,
    accountData,
    getMarginButtonStyle,
    marginType,
    displayLeverage,
    leverageUnavailableText,
    onUpdateMarginType,
    onUpdateLeverageSuccess,
    onLearnMore,
    isCross,
  };
}
